import type { ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { useAuth } from '../auth'

/**
 * Route guard for Studio surfaces. Renders children when the user has
 * `has_studio_access` (= is_superuser OR role='studio_viewer').
 * Otherwise shows a lock screen (instead of bouncing to login or 404,
 * since the user IS authenticated, just not authorized for this surface).
 *
 * The backend independently enforces tenant scoping + per-row
 * permissions on the underlying APIs — this is just the UI gate so
 * non-Studio users don't stumble in via direct URL.
 */
export default function RequireAdmin({ children }: { children: ReactNode }) {
  const auth = useAuth()
  if (auth.status !== 'authenticated') return null  // Layout handles loading + login redirect
  if (auth.user.has_studio_access) return <>{children}</>
  return (
    <div className="me-scroll grid h-full place-items-center p-8">
      <div className="me-card max-w-md p-6 text-center">
        <Lock size={20} className="mx-auto me-text-mute" />
        <div className="mt-3 text-[14px] font-medium me-text">Studio access required</div>
        <p className="mt-2 text-[12px] me-text-dim">
          This surface is restricted to platform admins and Studio viewers.
          If you think you should have access, ask your platform admin to
          grant your account the studio_viewer role.
        </p>
      </div>
    </div>
  )
}
